"use client";

import clsx from "clsx";
import { useRef, useState, type FocusEvent, type FormEvent } from "react";
import { submitForm } from "@/lib/forms";

/**
 * The contact form, rebuilt from the WPForms form the Contact page embedded
 * ([wpforms id="…"]). It keeps the plugin's ids, names and element structure,
 * is styled to look exactly as the theme dressed the plugin's output, and
 * validates the way the plugin's jQuery Validate setup did:
 *
 *  - Nothing is flagged until the first submit, or until a filled-in field is
 *    left (Validate's "lazy" focusout).
 *  - A flagged field re-checks as it is typed in, and clears once it passes.
 *  - Each error is an <em> after the input, and the input gets aria-invalid,
 *    which also turns its underline red.
 *  - The first invalid field takes focus on a failed submit.
 *
 * While sending, the button swaps to its data-alt-text. A failed submission
 * shows its message in a box above the button, as the plugin's ajax handler
 * did; a successful one replaces the form with the confirmation.
 *
 * Left out of the WPForms markup: its class names, the honeypot field (its
 * random label was the plugin's anti-spam), the hidden page and form id
 * inputs, the inline CSS variable block and the plugin's spinner image.
 */

const FORM_ID = 92;
const PREFIX = `wpforms-${FORM_ID}-field`;

const SUBMIT_TEXT = "Send enquiry";
const SUBMIT_ALT_TEXT = "Sending...";
const CONFIRMATION =
  "Thanks for contacting us! We will be in touch with you shortly.";

const REQUIRED_MESSAGE = "This field is required.";
const EMAIL_MESSAGE = "Please enter a valid email address.";

const ENQUIRY_TYPES = [
  "General enquiry",
  "Education partnership",
  "Industry partnership",
  "Local & Authorities",
  "Road Safety & Specialists",
  "Community partnership",
  "Press & media",
  "Other",
];

type FieldName = "name" | "email" | "organisation" | "enquiry" | "message";

type Values = Record<FieldName, string>;
type Errors = Partial<Record<FieldName, string>>;

/** WPForms field ids, which its markup builds every id and name from. */
const FIELD_IDS: Record<FieldName, number> = {
  name: 1,
  email: 2,
  organisation: 3,
  enquiry: 4,
  message: 5,
};

const REQUIRED: FieldName[] = ["name", "email", "message"];

const EMPTY: Values = {
  name: "",
  email: "",
  organisation: "",
  enquiry: "",
  message: "",
};

// The plugin's own check: something@something.something, no spaces.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A field row; its label darkens while anything in the row has focus. */
const FIELD = "group float-none m-0 w-full p-0";
const HALF_FIELD =
  "group float-none m-0 ml-0 w-[calc(50%-16px)] p-0 mobile:w-full";

/** Field labels, less their bottom margin, which is wider on the text fields than on the pill group. */
const LABEL = clsx(
  "m-0 block text-[10.5px] leading-[1.4] font-medium tracking-[0.14em] text-brand-blue/40 uppercase",
  "transition-[color] duration-180 ease-[ease] group-focus-within:text-brand-blue",
);

const TEXT_LABEL = clsx(LABEL, "mb-[9px]");

/** The underlined text inputs and the textarea. */
const CONTROL = clsx(
  "w-full max-w-full rounded-none border-0 border-b-[1.5px] border-brand-blue/13 bg-transparent px-0 pt-[9px] pb-[11px] shadow-none",
  "text-[15px] leading-[1.72] font-normal text-brand-blue transition-[border-color] duration-200 ease-[ease] placeholder:text-brand-blue/30",
  "focus:border-brand-blue focus:bg-transparent focus:shadow-none focus:outline-none",
  // A flagged field's underline turns red, bar while it has focus, as the theme's rules ranked them.
  "aria-invalid:not-focus:border-b-[#d63637]",
);

const INPUT = clsx(CONTROL, "h-auto");

// The WPForms stylesheet set this height on WordPress.
const TEXTAREA = clsx(CONTROL, "block h-[120px] resize-none");

/** The warning triangle before each error message, the WPForms stylesheet's icon. */
const ERROR_ICON =
  "before:[mask:url(data:image/svg+xml,%3Csvg%20width=%2717%27%20height=%2715%27%20viewBox=%270%200%2017%2015%27%20fill=%27none%27%20xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cpath%20d=%27M16.0264%2012.3086L9.46387%200.90625C8.97168%200.0585938%207.68652%200.03125%207.19434%200.90625L0.631836%2012.3086C0.139648%2013.1562%200.768555%2014.25%201.78027%2014.25H14.8779C15.8896%2014.25%2016.5186%2013.1836%2016.0264%2012.3086ZM8.34277%209.92969C9.02637%209.92969%209.60059%2010.5039%209.60059%2011.1875C9.60059%2011.8984%209.02637%2012.4453%208.34277%2012.4453C7.63184%2012.4453%207.08496%2011.8984%207.08496%2011.1875C7.08496%2010.5039%207.63184%209.92969%208.34277%209.92969ZM7.13965%205.41797C7.1123%205.22656%207.27637%205.0625%207.46777%205.0625H9.19043C9.38184%205.0625%209.5459%205.22656%209.51855%205.41797L9.32715%209.13672C9.2998%209.32812%209.16309%209.4375%208.99902%209.4375H7.65918C7.49512%209.4375%207.3584%209.32812%207.33105%209.13672L7.13965%205.41797Z%27%20fill=%27currentColor%27/%3E%3C/svg%3E)_no-repeat]";

/** The green tick in a tinted ring that opens the confirmation. */
const CONFIRMATION_ICON =
  "before:[background:rgba(101,173,44,0.07)_url(data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%2722%27%20height=%2722%27%20viewBox=%270%200%2022%2022%27%20fill=%27none%27%3E%3Cpath%20d=%27M4%2011l5%205%209-9%27%20stroke=%27%2365AD2C%27%20stroke-width=%272.2%27%20stroke-linecap=%27round%27%20stroke-linejoin=%27round%27/%3E%3C/svg%3E)_no-repeat_center]";

function validate(name: FieldName, value: string): string | undefined {
  if (REQUIRED.includes(name) && !value.trim()) {
    return REQUIRED_MESSAGE;
  }

  if (name === "email" && value.trim() && !EMAIL_PATTERN.test(value.trim())) {
    return EMAIL_MESSAGE;
  }

  return undefined;
}

export default function ContactForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [values, setValues] = useState<Values>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const setError = (name: FieldName, message: string | undefined) => {
    setErrors((current) => ({ ...current, [name]: message }));
  };

  const update = (name: FieldName, value: string) => {
    setValues((current) => ({ ...current, [name]: value }));

    // Validate's onkeyup: only a field already flagged re-checks as it changes.
    if (errors[name]) {
      setError(name, validate(name, value));
    }
  };

  const handleBlur =
    (name: FieldName) =>
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Validate's onfocusout: a blank field is left alone until the first submit.
      if (submitted || event.currentTarget.value !== "") {
        setError(name, validate(name, event.currentTarget.value));
      }
    };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (sending) {
      return;
    }

    setSubmitted(true);
    setFailure("");

    const found: Errors = {};

    (Object.keys(values) as FieldName[]).forEach((name) => {
      const message = validate(name, values[name]);

      if (message) {
        found[name] = message;
      }
    });

    setErrors(found);

    const firstInvalid = (Object.keys(FIELD_IDS) as FieldName[]).find(
      (name) => found[name],
    );

    if (firstInvalid) {
      formRef.current
        ?.querySelector<HTMLElement>(`#${PREFIX}_${FIELD_IDS[firstInvalid]}`)
        ?.focus();

      return;
    }

    setSending(true);

    const result = await submitForm("contact", {
      name: values.name.trim(),
      email: values.email.trim(),
      organisation: values.organisation.trim(),
      enquiry: values.enquiry,
      message: values.message.trim(),
    });

    setSending(false);

    if (result.ok) {
      setConfirmed(true);
      setValues(EMPTY);
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });

      return;
    }

    // Server-side messages for our own fields show against them; the rest goes above the button.
    if (result.errors) {
      const fieldErrors: Errors = {};

      (Object.keys(FIELD_IDS) as FieldName[]).forEach((name) => {
        if (result.errors?.[name]) {
          fieldErrors[name] = result.errors[name];
        }
      });

      setErrors(fieldErrors);
    }

    setFailure(result.message);
  };

  const inputProps = (name: FieldName) => {
    const id = `${PREFIX}_${FIELD_IDS[name]}`;
    const required = REQUIRED.includes(name);

    return {
      id,
      name: `wpforms[fields][${FIELD_IDS[name]}]`,
      value: values[name],
      required,
      "aria-errormessage": `${id}-error`,
      "aria-invalid": errors[name] ? true : undefined,
      onBlur: handleBlur(name),
    };
  };

  const error = (name: FieldName) =>
    errors[name] ? (
      <em
        id={`${PREFIX}_${FIELD_IDS[name]}-error`}
        className={clsx(
          "relative m-0 mt-1.5 block min-w-[120px] cursor-default bg-none p-0 text-[12px] leading-[1.5] font-normal tracking-normal text-[#d63637] normal-case not-italic",
          "before:relative before:top-[1.5px] before:right-[5px] before:inline-block before:h-3.5 before:w-4 before:bg-current before:content-['']",
          ERROR_ICON,
        )}
        role="alert"
        aria-label="Error message"
      >
        {errors[name]}
      </em>
    ) : null;

  const required = (
    <span className="ml-[3px] font-medium text-das-green" aria-hidden="true">
      *
    </span>
  );

  // The 24px above and below is the WPForms plugin's container margin, which beat
  // the theme's reset on staging. The top one collapses into the heading's; the
  // bottom one only shows once the columns stack on smaller screens.
  return (
    <div ref={containerRef} className="mx-0 my-6 p-0" id={`wpforms-${FORM_ID}`}>
      {confirmed ? (
        <div
          className={clsx(
            "m-0 border-0 bg-none px-0 pt-4 pb-0 text-[15px] leading-[1.78] text-[#5a6a7a]",
            "before:mb-7 before:block before:size-[52px] before:rounded-full before:border before:border-das-green/16 before:content-['']",
            CONFIRMATION_ICON,
          )}
          id={`wpforms-confirmation-${FORM_ID}`}
          role="alert"
        >
          <p className="m-0 mb-[15px] max-w-[400px] last:mb-0">
            {CONFIRMATION}
          </p>
        </div>
      ) : (
        <form
          ref={formRef}
          id={`wpforms-form-${FORM_ID}`}
          data-formid={FORM_ID}
          method="post"
          noValidate
          onSubmit={handleSubmit}
        >
          <div className="flex flex-wrap gap-x-8 gap-y-9 mobile:gap-y-7">
            <div
              id={`${PREFIX}_1-container`}
              className={HALF_FIELD}
              data-field-id="1"
            >
              <label className={TEXT_LABEL} htmlFor={`${PREFIX}_1`}>
                Full name {required}
              </label>
              <input
                type="text"
                placeholder="Your name"
                className={INPUT}
                {...inputProps("name")}
                onChange={(event) => update("name", event.target.value)}
              />
              {error("name")}
            </div>

            <div
              id={`${PREFIX}_2-container`}
              className={HALF_FIELD}
              data-field-id="2"
            >
              <label className={TEXT_LABEL} htmlFor={`${PREFIX}_2`}>
                Email address {required}
              </label>
              <input
                type="email"
                placeholder="you@organisation.com"
                spellCheck={false}
                className={INPUT}
                {...inputProps("email")}
                onChange={(event) => update("email", event.target.value)}
              />
              {error("email")}
            </div>

            <div
              id={`${PREFIX}_3-container`}
              className={FIELD}
              data-field-id="3"
            >
              <label className={TEXT_LABEL} htmlFor={`${PREFIX}_3`}>
                Organisation
              </label>
              <input
                type="text"
                placeholder="Institution, employer or authority"
                className={INPUT}
                {...inputProps("organisation")}
                onChange={(event) => update("organisation", event.target.value)}
              />
              {error("organisation")}
            </div>

            {/* The one field drawn differently: a group of pills, the real radios hidden behind their labels. */}
            <div
              id={`${PREFIX}_4-container`}
              className={FIELD}
              data-field-id="4"
            >
              <fieldset className="m-0 border-0 p-0">
                <legend className={clsx(LABEL, "mb-3")}>
                  Nature of enquiry
                </legend>
                <ul
                  id={`${PREFIX}_4`}
                  className="m-0 flex list-none flex-wrap gap-[7px] p-0"
                >
                  {ENQUIRY_TYPES.map((type, index) => {
                    const id = `${PREFIX}_4_${index + 1}`;

                    return (
                      <li
                        key={type}
                        className="m-0 block h-auto p-0 leading-[1.2]"
                      >
                        <input
                          type="radio"
                          id={id}
                          name="wpforms[fields][4]"
                          value={type}
                          checked={values.enquiry === type}
                          onChange={() => update("enquiry", type)}
                          className="peer pointer-events-none absolute m-0 h-px w-px border-0 p-0 opacity-0"
                        />
                        <label
                          className={clsx(
                            "m-0 inline-block cursor-pointer rounded-[100px] border border-brand-blue/13 px-[15px] py-2",
                            "text-[12.5px] leading-[1.2] font-medium tracking-[-0.005em] text-brand-blue/47",
                            "transition-[background-color,border-color,color] duration-180 ease-[ease]",
                            // Hovering a label hovers its radio. The checked fill outranks the hover, as in the theme.
                            "peer-[:hover:not(:checked)]:border-brand-blue/30 peer-[:hover:not(:checked)]:text-brand-blue/70",
                            "peer-checked:border-brand-blue peer-checked:bg-brand-blue peer-checked:text-white",
                            "peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-blue/40",
                          )}
                          htmlFor={id}
                        >
                          {type}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
              {error("enquiry")}
            </div>

            <div
              id={`${PREFIX}_5-container`}
              className={FIELD}
              data-field-id="5"
            >
              <label className={TEXT_LABEL} htmlFor={`${PREFIX}_5`}>
                Your message {required}
              </label>
              <textarea
                placeholder="Tell us about your interest in DAS Academies..."
                className={TEXTAREA}
                {...inputProps("message")}
                onChange={(event) => update("message", event.target.value)}
              />
              {error("message")}
            </div>
          </div>

          {failure && (
            <div
              className="mt-8 text-[14px] leading-[1.6] text-[#d63637]"
              role="alert"
            >
              <p className="m-0">{failure}</p>
            </div>
          )}

          {/* Outside the field rows, so it carries the row gap itself. */}
          <div className="mt-10 p-0 mobile:mt-8">
            <button
              type="submit"
              name="wpforms[submit]"
              id={`wpforms-submit-${FORM_ID}`}
              className={clsx(
                "inline-flex h-auto cursor-pointer items-center gap-2.5 rounded-[100px] border-0 bg-brand-blue bg-none px-7.5 py-3.5",
                "text-[14px] leading-[1.2] font-semibold tracking-[-0.01em] text-white [transition:opacity_0.2s,transform_0.15s]",
                "hover:opacity-82 hover:[transform:translateY(-1px)] active:opacity-82 active:[transform:translateY(-1px)]",
                "focus-visible:shadow-[0_0_0_3px_rgba(8,41,67,0.25)]",
              )}
              data-alt-text={SUBMIT_ALT_TEXT}
              data-submit-text={SUBMIT_TEXT}
              aria-live="assertive"
              value="wpforms-submit"
              disabled={sending}
            >
              {sending ? SUBMIT_ALT_TEXT : SUBMIT_TEXT}

              {/* The design's trailing arrow; it was a background image on ::after. */}
              <svg
                className="shrink-0"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M2 7h10M8 3l4 4-4 4"
                  stroke="#ffffff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
